import { ApiError, isApiErrorBody } from './errors'
import type {
  JoinOptions,
  JoinReport,
  Analysis,
  AnalysisApplyPayload,
  AnalysisOptions,
  AnalysisResult,
  AudioTrack,
  DetectLinesParams,
  DetectLinesResult,
  Health,
  Line,
  Project,
  ProjectFull,
  ProjectMode,
  Render,
  Role,
  ScenarioPayload,
  SegmentationOptions,
  Take,
  TrackKind,
} from './types'

/**
 * Базовый префикс. В dev пусто — Vite проксирует /api и /media на :8080
 * (см. vite.config.ts). В проде можно переопределить через VITE_API_BASE.
 */
export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '')

const DEFAULT_TIMEOUT_MS = 15_000

function url(path: string): string {
  return `${API_BASE}${path}`
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

function toApiError(res: Response, body: unknown): ApiError {
  if (isApiErrorBody(body)) {
    return new ApiError(body.error.code, body.error.message, res.status)
  }
  if (res.status === 404) return new ApiError('not_found', '', res.status)
  if (res.status >= 500) return new ApiError('internal', '', res.status)
  const detail = typeof body === 'string' && body.length < 300 ? body : ''
  return new ApiError('bad_request', detail || `HTTP ${res.status}`, res.status)
}

type RequestOptions = {
  method?: string
  body?: unknown
  signal?: AbortSignal
  timeoutMs?: number
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options
  const controller = new AbortController()
  const timer = window.setTimeout(() => {
    controller.abort(new DOMException('timeout', 'TimeoutError'))
  }, timeoutMs)
  const onAbort = (): void => controller.abort()
  signal?.addEventListener('abort', onAbort)

  let res: Response
  try {
    res = await fetch(url(path), {
      method,
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    })
  } catch {
    if (signal?.aborted) throw new ApiError('aborted', 'Запрос отменён')
    if (controller.signal.aborted) throw new ApiError('timeout', '')
    throw new ApiError('network', '')
  } finally {
    window.clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
  }

  const parsed = await parseBody(res)
  if (!res.ok) throw toApiError(res, parsed)
  return parsed as T
}

export type UploadHandle<T> = { promise: Promise<T>; abort: () => void }

/** Загрузка файла с прогрессом — fetch не умеет upload progress, поэтому XHR. */
function upload<T>(
  path: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
): UploadHandle<T> {
  const xhr = new XMLHttpRequest()
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', url(path), true)
    xhr.responseType = 'text'
    if (onProgress) {
      xhr.upload.onprogress = (e: ProgressEvent): void => {
        if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total)
      }
    }
    xhr.onerror = (): void => reject(new ApiError('network', ''))
    xhr.ontimeout = (): void => reject(new ApiError('timeout', ''))
    xhr.onabort = (): void => reject(new ApiError('aborted', 'Загрузка отменена'))
    xhr.onload = (): void => {
      let parsed: unknown = null
      try {
        parsed = xhr.responseText ? (JSON.parse(xhr.responseText) as unknown) : null
      } catch {
        parsed = xhr.responseText
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1)
        resolve(parsed as T)
        return
      }
      if (isApiErrorBody(parsed)) {
        reject(new ApiError(parsed.error.code, parsed.error.message, xhr.status))
      } else if (xhr.status >= 500) {
        reject(new ApiError('internal', '', xhr.status))
      } else {
        reject(new ApiError('bad_request', `HTTP ${xhr.status}`, xhr.status))
      }
    }
    xhr.send(form)
  })
  return { promise, abort: () => xhr.abort() }
}

// ---------------------------------------------------------------- служебные

export function health(signal?: AbortSignal): Promise<Health> {
  return request<Health>('/api/health', { signal, timeoutMs: 5000 })
}

// ----------------------------------------------------------------- проекты

export function listProjects(signal?: AbortSignal): Promise<Project[]> {
  return request<Project[]>('/api/projects', { signal })
}

export function createProject(title: string): Promise<Project> {
  return request<Project>('/api/projects', { method: 'POST', body: { title } })
}

export function getProject(id: string, signal?: AbortSignal): Promise<ProjectFull> {
  return request<ProjectFull>(`/api/projects/${encodeURIComponent(id)}`, { signal })
}

export function patchProject(
  id: string,
  patch: { title?: string; mode?: ProjectMode },
): Promise<Project> {
  return request<Project>(`/api/projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: patch,
  })
}

export function deleteProject(id: string): Promise<void> {
  return request<void>(`/api/projects/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// -------------------------------------------------------------------- видео

export function uploadVideo(
  projectId: string,
  file: File,
  duration: number | null,
  onProgress?: (fraction: number) => void,
): UploadHandle<Project> {
  const form = new FormData()
  form.append('file', file, file.name)
  // Фолбэк на случай, если на сервере нет ffprobe (см. docs/API.md).
  if (duration !== null && Number.isFinite(duration)) form.append('duration', String(duration))
  return upload<Project>(`/api/projects/${encodeURIComponent(projectId)}/video`, form, onProgress)
}

export function videoUrl(projectId: string): string {
  return url(`/media/projects/${encodeURIComponent(projectId)}/video`)
}

// ---------------------------------------------------------------- дорожки

/** Лимит загрузки дорожки — 512 МБ (docs/DUBBING.md, «Дорожки»). */
export const MAX_TRACK_BYTES = 512 * 1024 * 1024

export const TRACK_ACCEPT =
  'audio/wav,audio/flac,audio/mpeg,audio/mp4,audio/ogg,audio/webm,audio/aac,' +
  '.wav,.flac,.mp3,.m4a,.aac,.ogg,.opus,.webm'

export function listTracks(projectId: string, signal?: AbortSignal): Promise<AudioTrack[]> {
  return request<AudioTrack[]>(`/api/projects/${encodeURIComponent(projectId)}/tracks`, { signal })
}

export function uploadTrack(
  projectId: string,
  file: File,
  meta: { kind: TrackKind; label: string },
  onProgress?: (fraction: number) => void,
): UploadHandle<AudioTrack> {
  const form = new FormData()
  form.append('file', file, file.name)
  form.append('kind', meta.kind)
  form.append('label', meta.label)
  return upload<AudioTrack>(`/api/projects/${encodeURIComponent(projectId)}/tracks`, form, onProgress)
}

export function patchTrack(
  id: string,
  patch: { kind?: TrackKind; label?: string },
): Promise<AudioTrack> {
  return request<AudioTrack>(`/api/tracks/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch })
}

export function deleteTrack(id: string): Promise<void> {
  return request<void>(`/api/tracks/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function trackUrl(trackId: string): string {
  return url(`/media/tracks/${encodeURIComponent(trackId)}`)
}

// -------------------------------------------------------------------- роли

export function createRole(
  projectId: string,
  body: { name: string; color: string },
): Promise<Role> {
  return request<Role>(`/api/projects/${encodeURIComponent(projectId)}/roles`, {
    method: 'POST',
    body,
  })
}

export function patchRole(id: string, patch: { name?: string; color?: string }): Promise<Role> {
  return request<Role>(`/api/roles/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch })
}

export function deleteRole(id: string): Promise<void> {
  return request<void>(`/api/roles/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// ----------------------------------------------------------------- реплики

export type LineInput = {
  roleId: string
  start: number
  end: number
  text: string
  /** Оригинал реплики; старый бэкенд поле проигнорирует. */
  textOriginal?: string
}

export function createLine(projectId: string, body: LineInput): Promise<Line> {
  return request<Line>(`/api/projects/${encodeURIComponent(projectId)}/lines`, {
    method: 'POST',
    body,
  })
}

export function patchLine(id: string, patch: Partial<LineInput>): Promise<Line> {
  return request<Line>(`/api/lines/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch })
}

export function deleteLine(id: string): Promise<void> {
  return request<void>(`/api/lines/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function replaceScenario(projectId: string, payload: ScenarioPayload): Promise<ProjectFull> {
  return request<ProjectFull>(`/api/projects/${encodeURIComponent(projectId)}/lines`, {
    method: 'PUT',
    body: payload,
  })
}

// ------------------------------------------------- автоматическая разметка

/** Значения по умолчанию из docs/DUBBING.md. */
export const DETECT_DEFAULTS = {
  threshold: -35,
  minSilence: 0.35,
  minLine: 0.3,
  padding: 0.1,
} as const

/**
 * Эндпоинт только возвращает сегменты и ничего не создаёт — реплики заводит
 * клиент через replaceScenario() после подтверждения пользователем.
 * ffmpeg сканирует дорожку целиком, поэтому таймаут щедрый.
 */
export function detectLines(
  projectId: string,
  params: DetectLinesParams,
  signal?: AbortSignal,
): Promise<DetectLinesResult> {
  return request<DetectLinesResult>(
    `/api/projects/${encodeURIComponent(projectId)}/detect-lines`,
    { method: 'POST', body: params, signal, timeoutMs: 180_000 },
  )
}

// ------------------------------------------ автоматический разбор видео

/**
 * Значения по умолчанию для запуска разбора (docs/ANALYSIS.md, «POST /jobs»).
 * `speakers: null` — определять автоматически; указание числа заметно повышает
 * точность диаризации, поэтому интерфейс это предлагает.
 */
export const ANALYSIS_DEFAULTS: AnalysisOptions = {
  targetLanguage: 'ru',
  speakers: null,
  minSpeakers: null,
  maxSpeakers: null,
  translate: true,
  separate: true,
}

/**
 * Значения по умолчанию этапа сегментации (docs/SEGMENTATION.md, «Параметры»).
 * Ровно те же, что у сервиса: пока форма совпадает с этим объектом, блок
 * `segmentation` в запрос не кладётся вовсе.
 */
/**
 * Значения по умолчанию для склейки. Пока форма совпадает с ними, блок
 * `join` в запрос не кладётся — сервис решает сам.
 */
export const JOIN_DEFAULTS: JoinOptions = {
  mode: 'auto',
  maxGap: 0.6,
  maxDuration: 12,
}

/**
 * Склейка готового черновика без повторного разбора: подбор параметров не
 * должен стоить минут работы видеокарты (docs/BACKLOG.md, задача 1).
 */
export async function joinLines(
  lines: JoinInputLine[],
  params: JoinOptions,
): Promise<{ lines: JoinInputLine[]; join: JoinReport }> {
  return request('/api/join', { method: 'POST', body: { lines, params } })
}

/** Реплика в том виде, в каком её принимает и возвращает склейка. */
export type JoinInputLine = {
  start: number
  end: number
  speaker: string | null
  kind?: string
  textOriginal: string
  text: string
  confidence: number
}
export const SEGMENTATION_DEFAULTS: SegmentationOptions = {
  mode: 'vad+energy',
  vadThreshold: 0.5,
  energyThresholdDb: -45,
  minSpeech: 0.2,
  minSilence: 0.3,
  padding: 0.1,
  maxLineDuration: 12,
  keepNonverbal: true,
}

/** Ставит задачу разбора. 503 `analyzer_unavailable` — штатная ситуация. */
export function analyze(projectId: string, options: AnalysisOptions): Promise<Analysis> {
  return request<Analysis>(`/api/projects/${encodeURIComponent(projectId)}/analyze`, {
    method: 'POST',
    body: options,
    timeoutMs: 30_000,
  })
}

export function getAnalysis(id: string, signal?: AbortSignal): Promise<Analysis> {
  return request<Analysis>(`/api/analyses/${encodeURIComponent(id)}`, { signal, timeoutMs: 10_000 })
}

/** Результат доступен после status: done. Реплик может быть много — таймаут щедрый. */
export function getAnalysisResult(id: string, signal?: AbortSignal): Promise<AnalysisResult> {
  return request<AnalysisResult>(`/api/analyses/${encodeURIComponent(id)}/result`, {
    signal,
    timeoutMs: 60_000,
  })
}

export function cancelAnalysis(id: string): Promise<void> {
  return request<void>(`/api/analyses/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    timeoutMs: 30_000,
  })
}

/**
 * Применяет **отредактированный** результат, а не сохранённый: пользователь
 * обязан увидеть и поправить черновик до того, как он затрёт проект.
 * Ответ — слепок проекта; старый бэкенд может вернуть что угодно, поэтому
 * вызывающая сторона перечитывает проект, если слепка не пришло.
 */
export function applyAnalysis(
  id: string,
  payload: AnalysisApplyPayload,
): Promise<ProjectFull | null> {
  return request<ProjectFull | null>(`/api/analyses/${encodeURIComponent(id)}/apply`, {
    method: 'POST',
    body: payload,
    timeoutMs: 180_000,
  })
}

// ------------------------------------------------------------------- дубли

export type TakeUpload = {
  blob: Blob
  filename: string
  duration: number
  offset: number
  author: string
}

export function uploadTake(
  lineId: string,
  take: TakeUpload,
  onProgress?: (fraction: number) => void,
): UploadHandle<Take> {
  const form = new FormData()
  form.append('file', take.blob, take.filename)
  form.append('duration', String(take.duration))
  form.append('offset', String(take.offset))
  form.append('author', take.author)
  return upload<Take>(`/api/lines/${encodeURIComponent(lineId)}/takes`, form, onProgress)
}

export function patchTake(
  id: string,
  patch: { isActive?: boolean; gain?: number; offset?: number },
): Promise<Take> {
  return request<Take>(`/api/takes/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch })
}

export function deleteTake(id: string): Promise<void> {
  return request<void>(`/api/takes/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function takeUrl(takeId: string): string {
  return url(`/media/takes/${encodeURIComponent(takeId)}`)
}

// ------------------------------------------------------------------ рендер

export type RenderOptions = {
  /** По умолчанию — mode проекта. */
  mode?: ProjectMode
  /** дБ, режим mute. */
  originalGain?: number
  /** дБ, режим replace. */
  meGain?: number
}

export function startRender(projectId: string, options: RenderOptions = {}): Promise<Render> {
  const body: Record<string, string | number> = {}
  if (options.mode !== undefined) body.mode = options.mode
  if (options.originalGain !== undefined) body.originalGain = options.originalGain
  if (options.meGain !== undefined) body.meGain = options.meGain
  return request<Render>(`/api/projects/${encodeURIComponent(projectId)}/render`, {
    method: 'POST',
    body,
  })
}

export function getRender(id: string, signal?: AbortSignal): Promise<Render> {
  return request<Render>(`/api/renders/${encodeURIComponent(id)}`, { signal })
}

export function renderUrl(renderId: string): string {
  return url(`/media/renders/${encodeURIComponent(renderId)}`)
}

// --------------------------------------------------------- импорт / экспорт

export function exportUrl(projectId: string, scenarioOnly: boolean): string {
  const suffix = scenarioOnly ? '?scenario=1' : ''
  return url(`/api/projects/${encodeURIComponent(projectId)}/export${suffix}`)
}

export function importProject(
  file: File,
  onProgress?: (fraction: number) => void,
): UploadHandle<Project> {
  const form = new FormData()
  form.append('file', file, file.name)
  return upload<Project>('/api/projects/import', form, onProgress)
}
